import { Info, RotateCcw, Trash2 } from "lucide-react";
import { useEffect } from "react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import type { AnnouncementDeletion } from "../announcement-deletion";
import { describeDeletion } from "../sent-announcements";
import { useAdminCommand } from "../use-admin-command";

/**
 * Confirms "Delete for everyone" and files DELETE_EVENT_ANNOUNCEMENT.
 *
 * The dialog says what the command can and cannot do: the in-app copy goes
 * from every inbox, but a push notification a device has already shown is
 * out of reach. `sendCommandId` is the SEND_EVENT_ANNOUNCEMENT command that
 * sent it, `scheduled-{id}` for a booked one.
 */
export function DeleteForEveryoneDialog({
  eventId,
  uid,
  sendCommandId,
  title,
  onClose,
}: {
  eventId: string;
  uid: string;
  sendCommandId: string;
  title: string;
  onClose: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const busy = state.status === "submitting" || state.status === "watching";
  // Another admin's deletion of the same announcement is still running, so
  // this command settled at once without doing anything. Said in the dialog,
  // which stays open, rather than a success line that claims a deletion.
  const deferred = state.status === "succeeded" && state.deferred === true;

  useEffect(() => {
    if (state.status === "succeeded" && !deferred) onClose();
  }, [state.status, deferred, onClose]);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete this announcement for everyone?</DialogTitle>
        </DialogHeader>
        <div
          className="space-y-2 text-sm text-muted-foreground"
          data-testid="delete-for-everyone-text"
        >
          <p>
            &ldquo;{title}&rdquo; is removed from everyone&rsquo;s inbox in the
            app.
          </p>
          <p>
            Push notifications already shown on phones or computers cannot be
            recalled. This cannot be undone.
          </p>
        </div>
        {deferred ? (
          <p
            role="status"
            className="flex items-start gap-2 rounded-md border border-status-info bg-status-info/15 px-3 py-2 text-sm text-foreground"
            data-testid="delete-for-everyone-already"
          >
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Already being deleted. Another deletion of this announcement is
              still running; the list shows when it has finished.
            </span>
          </p>
        ) : (
          <AdminCommandFeedback
            state={state}
            onReset={reset}
            testId="delete-for-everyone"
          />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {deferred ? "Close" : "Keep it"}
          </Button>
          <Button
            variant="destructive"
            disabled={busy || deferred}
            data-testid="delete-for-everyone-confirm"
            onClick={() =>
              void submit("DELETE_EVENT_ANNOUNCEMENT", eventId, uid, {
                sendCommandId,
              })
            }
          >
            <Trash2 className="h-4 w-4" />
            Delete for everyone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What a sent row shows about "Delete for everyone": the note for a
 * deletion under way, done or stopped, and the button while one may be filed.
 * A stopped deletion offers "Try again", which files a new delete command;
 * the backend lets it take over from the one that gave up. Buttons are size
 * sm, 44px tall on a phone.
 */
export function DeletionActions({
  deletion,
  timeZone,
  canDelete,
  onDelete,
}: {
  deletion: AnnouncementDeletion | undefined;
  timeZone: string | undefined;
  canDelete: boolean;
  onDelete: () => void;
}) {
  const stopped = deletion?.state === "stopped";
  return (
    <>
      {deletion?.state === "deleting" && (
        <p
          className="mt-1 text-xs text-muted-foreground"
          data-testid="deleting-for-everyone"
        >
          Deleting for everyone…
        </p>
      )}
      {deletion?.state === "deleted" && (
        <p
          className="mt-1 text-xs text-muted-foreground"
          data-testid="deleted-for-everyone"
        >
          {describeDeletion(deletion, timeZone)}
        </p>
      )}
      {deletion?.state === "stopped" && (
        <p
          className="mt-1 break-words text-xs text-destructive-text"
          data-testid="deletion-stopped"
        >
          Deletion stopped: {deletion.reason}
        </p>
      )}
      {canDelete && (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid={
              stopped ? "delete-for-everyone-retry" : "delete-for-everyone"
            }
            onClick={onDelete}
          >
            {stopped ? (
              <RotateCcw className="h-4 w-4" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            {stopped ? "Try again" : "Delete for everyone"}
          </Button>
        </div>
      )}
    </>
  );
}
